import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, from, Observable, switchMap, tap, throwError, catchError } from 'rxjs';
import { SafeResourceUrl } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CertificateModalSignatureData } from 'src/app/shared/services/signature-nca-common-utils.service';
import { BpService } from '../../../../core/services/bp.service';
import { MetricsService } from '../../../../shared/metrics/metrics.service';
import { SelectTariffSignatureForm } from '../../sign-selected-tariff-page/sign-selected-tariff-page.model';
import { ConfirmBySmsComponent } from '../../../../shared/components/modals/confirm-by-sms/confirm-by-sms.component';
import { UsersService } from '../../../users/user.service';
import { ModalService } from '../../../../core/modal/modal.service';
import { KkmsApiService } from '../../../../core/modules/kkms/kkms-api.service';
import { getRouteParam } from '../../../../shared/utils/getRouteParam.utils';
import { Pages } from '../../../pages';

@UntilDestroy()
@Component({
  selector: 'tax-kkm-sign-pdf',
  templateUrl: './kkm-sign-pdf.component.html',
  styleUrls: ['./kkm-sign-pdf.component.sass'],
})

export class KkmSignPdfComponent implements OnInit {
  @Input() kkmForm?: SelectTariffSignatureForm;
  @Input() link?: SafeResourceUrl;

  data?: CertificateModalSignatureData;
  private kkmId$: Observable<number> = getRouteParam('id');

  constructor(
    private router: Router,
    private kkmsApiService: KkmsApiService,
    private bpService: BpService,
    private metricsService: MetricsService,
    private modalService: ModalService,
    private usersService: UsersService,
  ) { }

  ngOnInit(): void {
    this.kkmsApiService.fetchKkmChangeStatusDocument(this.bpService.getProcessIds().pid)
      .pipe(untilDestroyed(this))
      .subscribe((response: string) => this.data = {
        doc: {
          billingSignature: response,
        },
        type: 'SIGN',
      });
  }

  back(): void {
    combineLatest([
      this.bpService.sendMessageCurrentBp('changeTariffMessage'),
      this.kkmId$,
    ])
      .pipe(
        tap(([_, kkmId]) => {
          this.router.navigate([`${Pages.Kkms}/${kkmId}/tariffs`], {
            queryParams: this.bpService.getProcessIds(),
            replaceUrl: true,
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  emitYandexGoal(): void {
    this.metricsService.ymGoal('Открытие модального окна подписания заявки на оформление подписки');
  }

  setSignature(modalSignatureData: CertificateModalSignatureData): void {
    if (!this.kkmForm) return;

    this.kkmForm.fields.billingSignature = modalSignatureData.signature?.billingSignature || null;
    this.bpService.submitCurrentBp(this.kkmForm.fields)
      .pipe(
        catchError(this.catchBpError.bind(this)),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.metricsService.ymGoal('Успешное подписание заявки на оформление подписки');
          this.router.navigate([Pages.Kkms]);
        },
      });
  }

  signBySms(): void {
    this.usersService.getCurrentUserInfo()
      .pipe(
        switchMap(userInfo => this.modalService
          .open(ConfirmBySmsComponent, {
            title: 'sign.by.sms.title',
            buttonText: 'button.sign',
            businessProcessData: {
              IIN: userInfo.organisation.inn,
            },
            businessProcessMessage: 'signBySmsMessage',
          })),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.metricsService.ymGoal('Успешное подписание заявки на оформление подписки');
          this.router.navigate([Pages.Kkms]);
        },
      });
  }

  private catchBpError(error: HttpErrorResponse): Observable<unknown> {
    return combineLatest([
      this.bpService.getCurrentBpFormData(),
      this.kkmId$,
    ]).pipe(
      switchMap(([form, kkmId]) => {
        if (form.formKey === 'enter-tariff-manually') {
          return from(this.router.navigate([`${Pages.Kkms}/${kkmId}/tariffs`], {
            queryParams: this.bpService.getProcessIds(),
          }));
        }
        return throwError(() => new HttpErrorResponse({
          error,
        }));
      }),
    );
  }
}
